import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import localStorage from 'redux-persist/es/storage';
import * as _ from 'lodash';
import { ReactNode } from 'react';
import { AnswerType } from '../../utils/enums/answer-type.enum';

export interface TopBarViewProps {
  text?: string;
  path?: string; // where should navigate in onclick
  sideMenuPath?: string; // which side menu should enable (path of the side menu)
  button?: {
    text: string;
    color: 'primary' | 'error';
    icon?: ReactNode;
    onClick: () => void;
  };
  backButtonOnClick?: () => void; // define custom onClick function, if not default function navigate to back
  backButton?: {
    path: string;
  };
  backNav?: boolean;
  customElement?: ReactNode; // if customElement defined, keep above filed undefined
}

export interface NotificationPaneViewProp {
  isView: boolean;
}

export interface ISignUpData {
  lastActiveStep: number;
  questionOneAnswer?: AnswerType;
  questionTwoAnswer?: AnswerType;
  plan?: string;
  roles?: string[];
  businessEmail?: string;
}

export interface LogInPayload {
  email: string;
  password: string;
  previousPath?: string;
}

export interface ICredentials {
  username: string;
  firstName: string;
  familyName: string;
  businessEmail: string;
  confirmEmail: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  country: string;
  designationId: string;
  customDesignation: string;
  companyWebsite: string;
  isAgreedToTermsAndCondition: boolean;
}

export interface IOrganization {
  companyName: string;
  country: string;
  website: string;
  ownDevice: boolean;
  thirdPartyDevice: boolean;
  subscriptionPlan: string;
}

export interface ISignUpPayload {
  username: string;
  firstName: string;
  familyName: string;
  email: string;
  phone: string;
  password: string;
  designationId: number;
  customDesignation?: string;
  businessRole: string[];
  organization: IOrganization;
}

export interface IValidateOtpPayload {
  code: string;
  email: string;
}

export interface ICheckUsernameOrEmailTakenPayload {
  input: string;
  isUsername: boolean;
}

export interface IAuthSlice {
  isLogin: boolean;
  topBarView?: TopBarViewProps;
  componentStatus: { [name: string]: undefined | string | boolean };
  signUpData: ISignUpData;
  credentials: ICredentials;
  stripeAccountId?: string;
  accountStatus: string;
  userRole: string;
  isLoggedIn: boolean;
  organizationId?: string;
  isAccountInactive: boolean;
  accessToken?: string;
  refreshToken?: string;
  previousPath?: string;
}

const initialState: IAuthSlice = {
  isLogin: false,
  topBarView: undefined,
  componentStatus: {
    canUnlock: false,
    isEmailAlreadyExists: false,
    isUsernameAlreadyExists: false,
  },
  signUpData: {
    lastActiveStep: 0,
    businessEmail: '',
  },
  credentials: {
    username: '',
    firstName: '',
    familyName: '',
    businessEmail: '',
    confirmEmail: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    country: '',
    designationId: '',
    customDesignation: '',
    companyWebsite: '',
    isAgreedToTermsAndCondition: false,
  },
  stripeAccountId: undefined,
  accountStatus: '',
  userRole: '',
  isLoggedIn: false,
  organizationId: undefined,
  isAccountInactive: false,
  accessToken: undefined,
  refreshToken: undefined,
  previousPath: undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setComponentStatus(state, action: PayloadAction<{ [key: string]: undefined | string | boolean }>) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    resetIsEmailAlreadyExistsStatus(state) {
      state.componentStatus.isEmailAlreadyExists = false;
    },
    resetIsUserNameAlreadyExistsStatus(state) {
      state.componentStatus.isUsernameAlreadyExists = false;
    },
    clearComponentStatus(state, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    setTopBarView(state, action: PayloadAction<TopBarViewProps | undefined>) {
      state.topBarView = action.payload;
    },
    setLogInStatus(state, action: PayloadAction<boolean>) {
      state.isLogin = action.payload;
    },
    logInAsync: (state, _action: PayloadAction<LogInPayload>) => {
      state.previousPath = _action.payload.previousPath;
    },
    clearPreviousPath(state) {
      state.previousPath = undefined;
    },
    logOutAsync: (state) => {
      state.isLogin = false;
    },
    sendPasswordResetLinkAsync: (_state, _action: PayloadAction<{ email: string }>) => {},
    validatePasswordResetTokenAsync: (_state, _action: PayloadAction<{ token: string }>) => {},
    updatePasswordAsync: (
      _state,
      _action: PayloadAction<{ token: string; newPassword: string; isNewUser: boolean }>,
    ) => {},
    setSignUpData(state, action: PayloadAction<ISignUpData>) {
      const { lastActiveStep, questionOneAnswer, questionTwoAnswer, plan, roles, businessEmail } = action.payload;
      state.signUpData.lastActiveStep = lastActiveStep;
      if (questionOneAnswer) {
        state.signUpData.questionOneAnswer = questionOneAnswer;
      }
      if (questionTwoAnswer) {
        state.signUpData.questionTwoAnswer = questionTwoAnswer;
      }
      if (plan) {
        state.signUpData.plan = plan;
      }
      if (roles) {
        state.signUpData.roles = roles;
      }
      if (businessEmail) {
        state.signUpData.businessEmail = businessEmail;
      }
    },
    setCredentials: (state, action: PayloadAction<ICredentials>) => {
      state.credentials = action.payload;
    },
    setStripeAccountId(state, action: PayloadAction<string | undefined>) {
      state.stripeAccountId = action.payload;
    },
    signUpAsync: (state, action: PayloadAction<ISignUpPayload>) => {
      state.signUpData.businessEmail = action.payload.email;
    },
    validateOtpAsync: (_state, _action: PayloadAction<IValidateOtpPayload>) => {},
    validateNewEmailOtpAsync: (_state, _action: PayloadAction<IValidateOtpPayload>) => {},
    resendEmailOtpAsync: (_state, _action: PayloadAction<{ email: string }>) => {},
    resendNewEmailOtpAsync: (_state, _action: PayloadAction<{ email: string }>) => {},
    checkUsernameOrEmailTakenAsync(_state, _action: PayloadAction<ICheckUsernameOrEmailTakenPayload>) {},
    setAccountStatus(state, action: PayloadAction<string>) {
      state.accountStatus = action.payload;
    },
    setUserRole(state, action: PayloadAction<string>) {
      state.userRole = action.payload;
    },
    updateAccountStatus(state, action: PayloadAction<string>) {
      state.accountStatus = action.payload;
      if (action.payload === 'active') {
        state.isAccountInactive = false;
      }
    },
    setOrganizationId(state, action: PayloadAction<string>) {
      state.organizationId = action.payload;
    },
    handleInactiveAccount(state) {
      state.isAccountInactive = true;
    },
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
    removeTokens(state) {
      delete state.accessToken;
      delete state.refreshToken;
    },
  },
});
const authPersistConfig = {
  key: 'auth',
  storage: localStorage,
  whitelist: ['isLogin', 'signUpData', 'accountStatus', 'userRole', 'organizationId', 'stripeAccountId'],
};

const authActions = authSlice.actions;
const authReducer = persistReducer(authPersistConfig, authSlice.reducer);

export { authActions, authReducer };
